import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Send, 
  Video, 
  Phone, 
  MoreVertical,
  AtSign
} from 'lucide-react';
import { AdvisorAvatar } from '../components/AdvisorAvatar';
import { VoiceCallInterface } from '../components/VoiceCallInterface';
import { VideoCallInterface } from '../components/VideoCallInterface';
import { handleOrchestratorVoiceResponse } from '../lib/voiceService';
import { useAuth } from '../hooks/useAuth';
import { 
  getConversationMessages, 
  addMessage, 
  createConversation, 
  getAdvisors,
  supabase 
} from '../lib/supabase';

export const ChatInterface: React.FC = () => {
  const navigate = useNavigate();
  const { chatId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  // Memoize these values to prevent changes
  const chatType = searchParams.get('type') || 'group';
  const urlAdvisorIds = searchParams.get('advisors')?.split(',') || [];
  
  const [message, setMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [messages, setMessages] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [advisors, setAdvisors] = useState<any[]>([]);
  const [participantsWithDetails, setParticipantsWithDetails] = useState<any[]>([]);
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [showVoiceCall, setShowVoiceCall] = useState(false);
  const [voiceCurrentSpeaker, setVoiceCurrentSpeaker] = useState<string | null>(null);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Get user name from auth data
  const userName = user?.user_metadata?.full_name || 
                   user?.user_metadata?.name || 
                   user?.email?.split('@')[0] || 
                   'User';
  
  // Stable state for conversation data
  const [participants, setParticipants] = useState<any[]>([]);
  const [actualAdvisorIds, setActualAdvisorIds] = useState<string[]>([]);
  
  // Prevent multiple setup calls
  const setupCompleteRef = useRef(false);
  const isGroupChat = chatType === 'group';
  const isOneOnOne = chatType === 'individual';

  // Single setup effect that runs once
  useEffect(() => {
    if (!user || setupCompleteRef.current) return;
    
    const setupConversation = async () => {
      try {
        console.log('Setting up conversation (one time):', { chatId, chatType, urlAdvisorIds });
        
        // Validate required parameters
        if (!chatId) {
          console.error('No chat ID provided');
          setIsLoading(false);
          return;
        }
        
        // Load all advisors first
        const { data: advisorData } = await getAdvisors();
        console.log('Loaded advisor data:', advisorData?.length || 0, 'advisors');
        
        if (!advisorData) {
          console.error('No advisor data loaded');
          alert('Failed to load advisor data. Please check your connection and try again.');
          setIsLoading(false);
          return;
        }
        
        setAdvisors(advisorData);

        // Store complete advisor data for easy access
        console.log('Complete advisor data loaded:', advisorData);

        // Check if this is an existing conversation or create a new one
        if (chatId && chatId.startsWith('chat_')) {
          // This is a new conversation, create it
          if (urlAdvisorIds.length === 0) {
            console.error('No advisor IDs provided in URL for new conversation');
            setIsLoading(false);
            return;
          }
          
          console.log('Creating new conversation with advisor IDs:', urlAdvisorIds);
          
          // Find selected advisors
          const selectedAdvisors = advisorData.filter(advisor => 
            urlAdvisorIds.includes(advisor.id)
          );
          console.log('Selected advisors for new conversation:', selectedAdvisors.length);
          
          if (selectedAdvisors.length === 0) {
            console.error('No advisors found for the provided IDs:', urlAdvisorIds);
            console.log('Available advisor IDs:', advisorData.map(a => a.id));
            alert(`Selected advisors not found. Available advisors: ${advisorData.map(a => a.name).join(', ')}`);
            setIsLoading(false);
            return;
          }
          
          // Set stable participant data
          const participantData = selectedAdvisors.map(a => ({
            id: a.id,
            name: a.name,
            title: a.title,
            description: a.description,
            personality_prompt: a.personality_prompt,
            elevenlabs_agent_id: a.elevenlabs_agent_id,
            gender: a.gender,
            is_default: a.is_default,
            voice_settings: a.voice_settings
          }));
          
          setParticipants(participantData);
          setParticipantsWithDetails(participantData);
          setActualAdvisorIds(urlAdvisorIds);
          
          const conversationData = {
            user_id: user.id,
            title: `Chat with ${selectedAdvisors.map(a => a.name).join(', ')}`,
            advisor_ids: urlAdvisorIds,
            conversation_type: isGroupChat ? 'group' as const : 'individual' as const,
          };
          
          console.log('Creating new conversation:', conversationData);
          const { data: newConversation, error } = await createConversation(conversationData);
          if (error) throw error;
          
          setConversationId(newConversation.id);
          setMessages([]);
          
        } else if (chatId) {
          // This is an existing conversation, load messages
          console.log('Loading existing conversation:', chatId);
          
          const { data: conversationData } = await supabase
            .from('conversations')
            .select('advisor_ids, conversation_type, title')
            .eq('id', chatId)
            .single();
            
          if (!conversationData) {
            console.error('Conversation not found:', chatId);
            setIsLoading(false);
            return;
          }
          
          console.log('Loaded conversation advisor_ids:', conversationData.advisor_ids);
          
          // Find advisors for this conversation
          const selectedAdvisors = advisorData.filter(advisor => 
            conversationData.advisor_ids.includes(advisor.id)
          );
          console.log('Setting participants for existing conversation:', selectedAdvisors.length);
          
          if (selectedAdvisors.length === 0) {
            console.error('No matching advisors found for conversation advisor_ids:', conversationData.advisor_ids);
          }
          
          // Set stable participant data
          const participantData = selectedAdvisors.map(a => ({
            id: a.id,
            name: a.name,
            title: a.title,
            description: a.description,
            personality_prompt: a.personality_prompt,
            elevenlabs_agent_id: a.elevenlabs_agent_id,
            gender: a.gender,
            is_default: a.is_default,
            voice_settings: a.voice_settings
          }));
          
          setParticipants(participantData);
          setParticipantsWithDetails(participantData);
          setActualAdvisorIds(conversationData.advisor_ids);
          setConversationId(chatId);
          
          // Load messages
          const { data: messageData } = await getConversationMessages(chatId);
          if (messageData) {
            const formattedMessages = messageData.map(msg => ({
              id: msg.id,
              sender: msg.sender_type === 'user' ? 'User' : msg.advisors?.name || 'Advisor',
              content: msg.content,
              timestamp: new Date(msg.created_at).toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              }),
              isUser: msg.sender_type === 'user',
              advisorId: msg.sender_advisor_id
            }));
            setMessages(formattedMessages);
          }
        }
        
        setupCompleteRef.current = true;
        console.log('Setup completed successfully');
        
      } catch (error) {
        console.error('Failed to setup conversation:', error);
      } finally {
        setIsLoading(false);
      }
    };

    setupConversation();
  }, [user, chatId]); // Minimal dependencies

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Parse @mentions from message
  const parseMentions = (text: string) => {
    const mentionRegex = /@(\w+)/g;
    const mentions: string[] = [];
    let match;
    
    while ((match = mentionRegex.exec(text)) !== null) {
      const mentionedName = match[1].toLowerCase();
      const advisor = advisors.find(a => a.name.toLowerCase().includes(mentionedName));
      if (advisor) {
        mentions.push(advisor.id);
      }
    }
    
    return mentions;
  };

  // Handle @ mention input
  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    setMessage(text);
    
    // Check for @ mentions
    const cursorPosition = e.target.selectionStart;
    const textBeforeCursor = text.slice(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1 && lastAtIndex === textBeforeCursor.length - 1) {
      // User just typed @
      setShowMentionSuggestions(true);
      setMentionQuery('');
    } else if (lastAtIndex !== -1) {
      const afterAt = textBeforeCursor.slice(lastAtIndex + 1);
      if (!afterAt.includes(' ') && afterAt.length > 0) {
        // User is typing after @
        setShowMentionSuggestions(true);
        setMentionQuery(afterAt.toLowerCase());
      } else {
        setShowMentionSuggestions(false);
      }
    } else {
      setShowMentionSuggestions(false);
    }
  };

  // Insert mention
  const insertMention = (advisorName: string) => {
    const cursorPosition = message.length;
    const textBeforeCursor = message.slice(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    
    if (lastAtIndex !== -1) {
      const beforeAt = message.slice(0, lastAtIndex);
      const afterQuery = message.slice(cursorPosition);
      setMessage(`${beforeAt}@${advisorName} ${afterQuery}`);
    }
    
    setShowMentionSuggestions(false);
  };

  // Filter advisors for mentions
  const currentAdvisors = advisors.filter(advisor => 
    actualAdvisorIds.includes(advisor.id) && 
    advisor.name.toLowerCase().includes(mentionQuery)
  );
  
  const otherAdvisors = advisors.filter(advisor =>
    !actualAdvisorIds.includes(advisor.id) && 
    advisor.name.toLowerCase().includes(mentionQuery)
  );

  const sendMessage = async () => {
    if (message.trim()) {
      if (!conversationId || !user) return;
      
      setIsSending(true);
      
      // Parse mentions from the message
      const mentionedAdvisorIds = parseMentions(message);
      
      const newMessage = {
        id: Date.now().toString(),
        sender: 'User',
        content: message,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isUser: true
      };
      
      setMessages(prev => [...prev, newMessage]);
      const messageText = message;
      setMessage(''); // Clear input immediately

      try {
        console.log('Sending message to conversation:', conversationId);
        
        // Call the chat completion edge function
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-completion`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            conversationId,
            userMessage: messageText,
            userId: user.id,
            mentionedAdvisorIds: mentionedAdvisorIds.length > 0 ? mentionedAdvisorIds : undefined
          })
        });

        console.log('API response status:', response.status);
        
        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`);
        }
        
        const result = await response.json();
        console.log('API response result:', result);
        
        if (result.success) {
          // Handle multiple responses if present
          if (result.responses && Array.isArray(result.responses)) {
            // Multiple advisor responses
            const aiResponses = result.responses.map((resp: any, index: number) => ({
              id: resp.message_id || (Date.now() + index + 1).toString(),
              sender: resp.advisor.name,
              content: resp.response,
              timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              isUser: false,
              advisorId: resp.advisor.id
            }));
            
            // Add responses with a small delay between them for better UX
            aiResponses.forEach((response, index) => {
              setTimeout(() => {
                setMessages(prev => [...prev, response]);
              }, index * 1500); // 1.5 second delay between responses
            });
            
            // If voice call is active, trigger voice responses
            if (showVoiceCall && participants.length > 0) {
              const voiceResponses = result.responses.map((resp: any) => ({
                persona_name: resp.advisor.name,
                response_text: resp.response
              }));
              
              // Execute voice responses using the new voice service
              handleOrchestratorVoiceResponse(
                voiceResponses,
                participantsWithDetails,
                userName,
                setVoiceCurrentSpeaker,
                setVoiceError
              ).catch(error => {
                console.error('Voice response failed:', error);
                setVoiceError(error.message);
              });
            }
          } else {
            // Single advisor response (backward compatibility)
            const aiResponse = {
              id: result.message_id || (Date.now() + 1).toString(),
              sender: result.advisor.name,
              content: result.response,
              timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              isUser: false,
              advisorId: result.advisor.id
            };
            console.log('Adding AI response:', aiResponse);
            setMessages(prev => [...prev, aiResponse]);
            
            // If voice call is active, trigger voice response
            if (showVoiceCall && participants.length > 0) {
              const voiceResponse = [{
                persona_name: result.advisor.name,
                response_text: result.response
              }];
              
              // Execute voice response using the new voice service
              handleOrchestratorVoiceResponse(
                voiceResponse,
                participantsWithDetails,
                userName,
                setVoiceCurrentSpeaker,
                setVoiceError
              ).catch(error => {
                console.error('Voice response failed:', error);
                setVoiceError(error.message);
              });
            }
          }
        } else {
          // Handle error - show error message
          console.error('API returned error:', result.error);
          const errorMessage = {
            id: (Date.now() + 1).toString(),
            sender: 'System',
            content: `Sorry, there was an error: ${result.error}. Please try again.`,
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            isUser: false
          };
          setMessages(prev => [...prev, errorMessage]);
        }
      } catch (error) {
        console.error('Failed to send message:', error);
        const errorMessage = {
          id: (Date.now() + 1).toString(),
          sender: 'System',
          content: `Connection error: ${error.message}. Please check your internet and try again.`,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          isUser: false
        };
        setMessages(prev => [...prev, errorMessage]);
      } finally {
        setIsSending(false);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startVideoCall = () => {
    if (isGroupChat) return; // No video calls for group chats
    setShowVideoCall(true);
  };

  const startVoiceCall = () => {
    // Only start voice call if we have participants and conversation ID
    if (participantsWithDetails.length > 0 && conversationId && user) {
      console.log('Starting voice call with participants:', participantsWithDetails);
      setShowVoiceCall(true);
    } else {
      console.error('Cannot start voice call:', { 
        participantsLength: participantsWithDetails.length, 
        conversationId, 
        userId: user?.id 
      });
      alert('Unable to start voice call. Please wait for the conversation to load.');
    }
  };

  const endVoiceCall = () => {
    setShowVoiceCall(false);
    setVoiceCurrentSpeaker(null);
    setVoiceError(null);
  };

  const endVideoCall = () => {
    setShowVideoCall(false);
  };

  const toggleRecording = () => {
    setIsRecording(!isRecording);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full border-4 border-orange-400/20 border-t-orange-400 animate-spin mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading conversation...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header - Fixed */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-black/90 backdrop-blur-sm border-b border-white/10 px-4 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
            <button
              onClick={() => navigate('/boardroom')}
              className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-white transition-all duration-300 flex-shrink-0"
            >
              <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              <div className="flex -space-x-1 sm:-space-x-2 flex-shrink-0">
                {participants.map((participant) => (
                  <AdvisorAvatar key={participant.name} name={participant.name} size="small" />
                ))}
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-sm sm:text-lg font-bold text-white truncate">
                  {participants.length > 0 ? participants.map(p => p.name).join(', ') : 'Loading...'}
                </h1>
                <p className="text-white/60 text-xs sm:text-sm">
                  {isOneOnOne ? '1:1 Session' : `Group Chat • ${participants.length} advisors`}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {/* Voice Call Button - Always available */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={startVoiceCall}
              disabled={participants.length === 0}
              className={`p-2 sm:p-3 rounded-xl transition-all duration-300 ${
                participants.length > 0
                  ? 'bg-gradient-to-r from-green-500 to-blue-500 text-white hover:from-green-400 hover:to-blue-400'
                  : 'bg-white/10 text-white/50 cursor-not-allowed'
              }`}
              title="Start voice call"
            >
              <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
            </motion.button>

            {/* Video Call Button - Only for 1:1 */}
            {isOneOnOne && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={startVideoCall}
                disabled={participants.length === 0}
                className={`p-2 sm:p-3 rounded-xl transition-all duration-300 ${
                  participants.length > 0
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-400 hover:to-pink-400'
                    : 'bg-white/10 text-white/50 cursor-not-allowed'
                }`}
                title="Start video call"
              >
                <Video className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.button>
            )}

            <button className="p-2 sm:p-3 rounded-xl bg-white/5 hover:bg-white/10 text-white transition-all duration-300">
              <MoreVertical className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Messages - Scrollable with padding for fixed header/footer */}
      <div className="flex-1 overflow-y-auto pt-20 pb-32 px-4 sm:px-6 space-y-4 sm:space-y-6">
        <AnimatePresence>
          {messages.map((msg) => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${msg.isUser ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`flex items-start gap-2 sm:gap-3 max-w-[85%] sm:max-w-2xl ${msg.isUser ? 'flex-row-reverse' : ''}`}>
                {!msg.isUser && (
                  <AdvisorAvatar name={msg.sender} size="small" className="flex-shrink-0" />
                )}
                
                <div className={`flex flex-col ${msg.isUser ? 'items-end' : 'items-start'}`}>
                  {!msg.isUser && (
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-orange-400 font-semibold text-sm">{msg.sender}</span>
                      <span className="text-white/40 text-xs">{msg.timestamp}</span>
                    </div>
                  )}
                  
                  <div className={`p-3 sm:p-4 rounded-2xl ${
                    msg.isUser
                      ? 'bg-gradient-to-r from-orange-500 to-purple-500 text-white'
                      : 'bg-white/10 backdrop-blur-sm text-white border border-white/20'
                  } ${msg.isUser ? 'rounded-br-md' : 'rounded-bl-md'}`}>
                    <p className="leading-relaxed text-sm sm:text-base">{msg.content}</p>
                  </div>
                  
                  {msg.isUser && (
                    <span className="text-white/40 text-xs mt-1">{msg.timestamp}</span>
                  )}

                  {!msg.isUser && isOneOnOne && (
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={startVideoCall}
                        className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition-all duration-300"
                        title={`Video call with ${msg.sender}`}
                      >
                        <Video className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area - Fixed */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-black/90 backdrop-blur-sm border-t border-white/10 p-4 sm:p-6">
        <div className="flex items-end gap-3 sm:gap-4">
          <div className="flex-1 relative">
            <textarea
              value={message}
              onChange={handleMessageChange}
              onKeyPress={handleKeyPress}
              placeholder="Ask your advisory board anything... Use @advisor to mention specific advisors"
              className="w-full px-4 sm:px-6 py-3 sm:py-4 pr-10 sm:pr-12 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/60 resize-none focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-300 text-sm sm:text-base"
              rows={1}
              style={{ minHeight: '48px', maxHeight: '120px' }}
            />
            <button className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 p-1 text-white/40 hover:text-white/70 transition-colors duration-300">
              <AtSign className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
          
          {/* Mention Suggestions */}
          {showMentionSuggestions && (
            <div className="absolute bottom-full left-0 right-0 mb-2 bg-black/90 backdrop-blur-sm border border-white/20 rounded-2xl max-h-64 overflow-y-auto">
              {/* Current Advisors in Chat */}
              {currentAdvisors.length > 0 && (
                <>
                  <div className="px-3 py-2 border-b border-white/10">
                    <span className="text-white/60 text-xs font-medium uppercase tracking-wide">In this chat</span>
                  </div>
                  {currentAdvisors.map((advisor) => (
                    <button
                      key={advisor.id || advisor.name}
                      onClick={() => insertMention(advisor.name)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-white/10 transition-colors duration-300"
                    >
                      <AdvisorAvatar name={advisor.name} size="small" />
                      <div className="text-left">
                        <div className="text-white font-medium">{advisor.name}</div>
                        <div className="text-white/60 text-sm">{advisor.title}</div>
                      </div>
                    </button>
                  ))}
                </>
              )}
              
              {/* Other Available Advisors */}
              {otherAdvisors.length > 0 && (
                <>
                  {currentAdvisors.length > 0 && <div className="border-t border-white/10"></div>}
                  <div className="px-3 py-2 border-b border-white/10">
                    <span className="text-white/60 text-xs font-medium uppercase tracking-wide">Add to chat</span>
                  </div>
                  {otherAdvisors.map((advisor) => (
                    <button
                      key={advisor.id}
                      onClick={() => insertMention(advisor.name)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-white/10 transition-colors duration-300"
                    >
                      <AdvisorAvatar name={advisor.name} size="small" />
                      <div className="text-left flex-1">
                        <div className="text-white font-medium">{advisor.name}</div>
                        <div className="text-white/60 text-sm">{advisor.title}</div>
                      </div>
                      <div className="text-orange-400 text-xs">+Add</div>
                    </button>
                  ))}
                </>
              )}
              
              {/* No matches */}
              {currentAdvisors.length === 0 && otherAdvisors.length === 0 && mentionQuery && (
                <button
                  disabled
                  className="w-full p-3 text-white/60 text-sm"
                >
                  No advisors found matching "{mentionQuery}"
                </button>
              )}
            </div>
          )}
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={sendMessage}
            disabled={!message.trim() || isSending}
            className={`p-3 sm:p-4 rounded-2xl transition-all duration-300 ${
              message.trim() && !isSending
                ? 'bg-gradient-to-r from-orange-500 to-purple-500 text-white hover:from-orange-400 hover:to-purple-400'
                : 'bg-white/10 text-white/50 cursor-not-allowed'
            }`}
          >
            {isSending ? (
              <div className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-white/20 border-t-white animate-spin rounded-full" />
            ) : (
              <Send className="w-5 h-5 sm:w-6 sm:h-6" />
            )}
          </motion.button>
        </div>

        <div className="flex items-center justify-between mt-3 sm:mt-4">
          <div className="text-white/60 text-xs sm:text-sm">
            {isOneOnOne ? '1:1 Chat • Voice & Video available' : 'Group Chat • Voice available'}
          </div>

          <div className="text-white/40 text-xs">
            {participants.length > 0 ? `${participants.length} advisor${participants.length > 1 ? 's' : ''} in this chat` : 'Loading advisors...'}
          </div>
        </div>
      </div>

      {/* Voice Call Interface */}
      <AnimatePresence>
        {showVoiceCall && participantsWithDetails.length > 0 && (
          <VoiceCallInterface
            show={showVoiceCall}
            participants={participantsWithDetails}
            conversationId={conversationId || ''}
            userId={user?.id || ''}
            userName={userName}
            isRecording={isRecording}
            onToggleRecording={toggleRecording}
            onEndCall={endVoiceCall}
          />
        )}
      </AnimatePresence>

      {/* Video Call Interface - Only for 1:1 */}
      <AnimatePresence>
        {showVideoCall && isOneOnOne && participantsWithDetails.length > 0 && (
          <VideoCallInterface
            show={showVideoCall}
            advisorName={participantsWithDetails[0]?.name || 'Advisor'}
            onEndCall={endVideoCall}
          />
        )}
      </AnimatePresence>
    </div>
  );
};